import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Classification, Role } from "../../../../../api";
import { SACCIONES } from "../../../../../utils/constants";
import {
  PERMISSION_MENU_TREE,
  getAllActionKeys,
  getActionKey,
  getAvailableActions,
  getMenuLeafByKey,
} from "../../../../../utils/permissions";

import {
  generateRoleCode,
  getRolePayload,
  initialValues,
} from "./CreateRolePage.form";

import styles from "./CreateRolePage.module.scss";

function getRoleResponse(result) {
  return result?.role || result?.response || result || {};
}

function mapRoleToForm(role = {}) {
  return {
    ...initialValues,
    id: role.id || "",
    name: role.name || "",
    description: role.description || "",
    actionPermissions: Array.isArray(role.actionPermissions)
      ? role.actionPermissions
      : [],
  };
}

function findFirstLeafKey(nodes) {
  for (const node of nodes) {
    if (node.children) {
      const childKey = findFirstLeafKey(node.children);

      if (childKey) {
        return childKey;
      }
    } else {
      return node.key;
    }
  }

  return null;
}

const PERMISSION_TABS = [
  { key: "menu", label: "Permisos por Menú" },
  { key: "reports", label: "Permisos por Reporte" },
];

// Código de la clasificación "Visualizar" dentro del esquema SACCIONES:
// es la acción base — sin ella el rol no puede ver ese menú, así que
// marcar cualquier otra acción la marca también, y quitarla quita las
// demás acciones de ese menú.
const VIEW_ACTION_CODE = "VIS";

export function CreateRolePage() {
  const { roleId } = useParams();
  const isEditMode = Boolean(roleId);

  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("menu");
  const [selectedMenuKey, setSelectedMenuKey] = useState(() =>
    findFirstLeafKey(PERMISSION_MENU_TREE)
  );
  const [expandedGroups, setExpandedGroups] = useState(
    () =>
      new Set(
        PERMISSION_MENU_TREE.filter((node) => node.children).map(
          (node) => node.key
        )
      )
  );
  const [formValues, setFormValues] = useState(initialValues);

  const [actionList, setActionList] = useState([]);
  const [isLoadingActions, setIsLoadingActions] = useState(true);
  const [actionsError, setActionsError] = useState("");

  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");

  // Las acciones (Visualizar/Agregar/Modificar/Eliminar/Aprobar) vienen
  // del esquema de catálogo "SACCIONES" — no hay ninguna lista fija en
  // el código. Si
  // el esquema todavía no existe (falta correr
  // server/scripts/install-permission-scheme.js), se avisa y no se
  // puede marcar ninguna acción hasta que se instale.
  useEffect(() => {
    async function loadActions() {
      try {
        setIsLoadingActions(true);
        setActionsError("");

        const classificationCtrl = new Classification();
        const result =
          await classificationCtrl.getClassificationsByScheme(SACCIONES);

        const classifications = Array.isArray(result?.classifications)
          ? result.classifications
          : [];

        if (classifications.length === 0) {
          setActionList([]);
          setActionsError(
            `El esquema "${SACCIONES}" existe pero no tiene clasificaciones.`
          );
          return;
        }

        setActionList(
          classifications.map((classification) => ({
            key: classification.id,
            label: classification.name,
          }))
        );
      } catch (errorResponse) {
        console.error("loadActions error:", errorResponse);
        setActionList([]);
        setActionsError(
          errorResponse?.msg ||
            `No fue posible cargar las acciones del esquema "${SACCIONES}".`
        );
      } finally {
        setIsLoadingActions(false);
      }
    }

    loadActions();
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    async function loadRole() {
      try {
        setIsLoading(true);
        setServerError("");

        const roleCtrl = new Role();
        const result = await roleCtrl.getRole(roleId);
        const role = getRoleResponse(result);

        if (!role?._id) {
          throw new Error("No se encontró el rol solicitado.");
        }

        setFormValues(mapRoleToForm(role));
      } catch (errorResponse) {
        console.error("loadRole error:", errorResponse);

        setServerError(
          errorResponse?.msg || "No fue posible cargar el rol solicitado."
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadRole();
  }, [isEditMode, roleId]);

  const hasValidationErrors = Object.values(validationErrors).some(Boolean);

  function clearFieldError(fieldName) {
    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      [fieldName]: "",
    }));
  }

  function handleFieldChange(fieldName, value) {
    setFormValues((currentValues) => {
      const nextValues = { ...currentValues, [fieldName]: value };

      // El código es un dato interno: mientras se crea el rol, se genera
      // solo a partir del nombre. En edición se conserva el que ya tenía.
      if (fieldName === "name" && !isEditMode) {
        nextValues.id = generateRoleCode(value);
      }

      return nextValues;
    });

    clearFieldError(fieldName);
  }

  function handleCancel() {
    if (!isSubmitting) {
      navigate("/admin/roles");
    }
  }

  function handleToggleAction(actionKey, menuKey, actionCode) {
    setFormValues((currentValues) => {
      const isChecked = currentValues.actionPermissions.includes(actionKey);

      if (isChecked && actionCode === VIEW_ACTION_CODE) {
        // Sin "Visualizar" no tiene sentido dejar otras acciones
        // marcadas para ese menú: se quitan todas juntas.
        const prefix = `${menuKey}.`;

        return {
          ...currentValues,
          actionPermissions: currentValues.actionPermissions.filter(
            (key) => !key.startsWith(prefix)
          ),
        };
      }

      if (!isChecked && actionCode !== VIEW_ACTION_CODE) {
        // Marcar cualquier otra acción implica poder ver el menú.
        const viewActionKey = getActionKey(menuKey, VIEW_ACTION_CODE);
        const nextActionPermissions = [
          ...currentValues.actionPermissions,
          actionKey,
        ];

        if (!nextActionPermissions.includes(viewActionKey)) {
          nextActionPermissions.push(viewActionKey);
        }

        return {
          ...currentValues,
          actionPermissions: nextActionPermissions,
        };
      }

      return {
        ...currentValues,
        actionPermissions: isChecked
          ? currentValues.actionPermissions.filter((key) => key !== actionKey)
          : [...currentValues.actionPermissions, actionKey],
      };
    });
  }

  function handleSelectAllPermissions() {
    setFormValues((currentValues) => ({
      ...currentValues,
      actionPermissions: getAllActionKeys(actionList),
    }));
  }

  function handleClearAllPermissions() {
    setFormValues((currentValues) => ({
      ...currentValues,
      actionPermissions: [],
    }));
  }

  function validateForm() {
    const errors = {};

    if (!formValues.name.trim()) {
      errors.name = "El nombre del rol es obligatorio.";
    } else if (!generateRoleCode(formValues.name)) {
      errors.name =
        "El nombre debe incluir al menos una letra o número.";
    }

    return errors;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setServerError("");
      return;
    }

    try {
      setIsSubmitting(true);
      setServerError("");

      const roleCtrl = new Role();
      const payload = getRolePayload(formValues);

      if (isEditMode) {
        await roleCtrl.updateRole(roleId, payload);
      } else {
        await roleCtrl.createRole(payload);
      }

      navigate("/admin/roles", {
        state: {
          success: isEditMode
            ? "Rol actualizado correctamente."
            : "Rol creado correctamente.",
        },
      });
    } catch (errorResponse) {
      console.error("handleSubmit error:", errorResponse);

      setServerError(
        errorResponse?.msg ||
          "No fue posible guardar el rol. Intenta nuevamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function getMenuActionCount(menuKey) {
    const prefix = `${menuKey}.`;

    return formValues.actionPermissions.filter((key) =>
      key.startsWith(prefix)
    ).length;
  }

  function getGroupActionCount(node) {
    if (!node.children) {
      return getMenuActionCount(node.key);
    }

    return node.children.reduce(
      (total, childNode) => total + getGroupActionCount(childNode),
      0
    );
  }

  function handleToggleGroup(groupKey) {
    setExpandedGroups((currentExpanded) => {
      const nextExpanded = new Set(currentExpanded);

      if (nextExpanded.has(groupKey)) {
        nextExpanded.delete(groupKey);
      } else {
        nextExpanded.add(groupKey);
      }

      return nextExpanded;
    });
  }

  // Árbol expandible/colapsable (igual que la navegación del sidebar):
  // los grupos como "Catálogo" se pueden plegar, y sus menús hijos
  // aparecen indentados y unidos por una guía vertical.
  function renderMenuTreeNode(node) {
    if (node.children) {
      const isExpanded = expandedGroups.has(node.key);
      const groupActionCount = getGroupActionCount(node);

      return (
        <div key={node.key} className={styles.MenuTreeGroup}>
          <button
            type="button"
            className={styles.MenuTreeGroupToggle}
            onClick={() => handleToggleGroup(node.key)}
            aria-expanded={isExpanded}
          >
            <span
              className={
                isExpanded
                  ? styles.MenuTreeChevronOpen
                  : styles.MenuTreeChevron
              }
              aria-hidden="true"
            >
              ▸
            </span>

            <span className={styles.MenuTreeItemLabel}>{node.label}</span>

            {groupActionCount > 0 && (
              <span className={styles.MenuTreeItemBadge}>
                {groupActionCount}
              </span>
            )}
          </button>

          {isExpanded && (
            <div className={styles.MenuTreeChildren}>
              {node.children.map((childNode) =>
                renderMenuTreeNode(childNode)
              )}
            </div>
          )}
        </div>
      );
    }

    const permissionCount = getMenuActionCount(node.key);

    return (
      <button
        key={node.key}
        type="button"
        className={
          selectedMenuKey === node.key
            ? styles.MenuTreeItemActive
            : styles.MenuTreeItem
        }
        onClick={() => setSelectedMenuKey(node.key)}
      >
        <span className={styles.MenuTreeItemLabel} title={node.label}>
          {node.label}
        </span>

        {permissionCount > 0 && (
          <span className={styles.MenuTreeItemBadge}>{permissionCount}</span>
        )}
      </button>
    );
  }

  const selectedMenuNode = selectedMenuKey
    ? getMenuLeafByKey(selectedMenuKey)
    : null;

  if (isLoading) {
    return (
      <section className={styles.CreateRolePage}>
        <div className={styles.StateMessage}>Cargando rol...</div>
      </section>
    );
  }

  return (
    <section className={styles.CreateRolePage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={isSubmitting}
            aria-label="Volver a roles"
          >
            ←
          </button>

          <div>
            <p className={styles.Breadcrumb}>
              Roles / {isEditMode ? "Editar rol" : "Nuevo rol"}
            </p>

            <h1>{isEditMode ? "Editar rol" : "Nuevo rol"}</h1>

            <p className={styles.Subtitle}>
              {isEditMode
                ? "Actualiza el nombre, la descripción y los permisos del rol."
                : "Define el rol y qué menús y acciones puede usar."}
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>

          <button
            type="submit"
            form="create-role-form"
            className={styles.PublishButton}
            disabled={isSubmitting || hasValidationErrors}
          >
            {isSubmitting
              ? "Guardando..."
              : isEditMode
                ? "Guardar cambios"
                : "Crear rol"}
          </button>
        </div>
      </header>

      {serverError && (
        <div className={styles.ServerErrorMessage} role="alert">
          <span className={styles.MessageIcon} aria-hidden="true">
            !
          </span>

          <span>{serverError}</span>
        </div>
      )}

      <form
        id="create-role-form"
        className={styles.Form}
        onSubmit={handleSubmit}
        noValidate
      >
        <section className={styles.InfoCard}>
          <header className={styles.CardHeader}>
            <h2>Información del rol</h2>
          </header>

          <div className={styles.CardBody}>
            <div className={styles.FormGrid}>
              <label className={styles.Field}>
                <span>Nombre</span>

                <input
                  type="text"
                  value={formValues.name}
                  onChange={(event) =>
                    handleFieldChange("name", event.target.value)
                  }
                  placeholder="Ej. Vendedor"
                  aria-invalid={Boolean(validationErrors.name)}
                />

                {validationErrors.name && (
                  <span className={styles.FieldError}>
                    {validationErrors.name}
                  </span>
                )}
              </label>

              <label className={styles.Field}>
                <span>Descripción</span>

                <input
                  type="text"
                  value={formValues.description}
                  onChange={(event) =>
                    handleFieldChange("description", event.target.value)
                  }
                  placeholder="Descripción opcional"
                />
              </label>
            </div>
          </div>
        </section>

        <section className={styles.PermissionsCard}>
          <header className={styles.CardHeader}>
            <h2>Configuración de permisos</h2>
          </header>

          <div className={styles.CardBody}>
            <div className={styles.Tabs} role="tablist">
              {PERMISSION_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  className={
                    activeTab === tab.key ? styles.TabActive : styles.Tab
                  }
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "menu" && (
              <div className={styles.TabPanel}>
                <div className={styles.PermissionsToolbar}>
                  <p>
                    Selecciona un menú de la lista y marca qué acciones
                    puede ejecutar el rol dentro de él.
                  </p>

                  <div className={styles.PermissionsToolbarActions}>
                    <button
                      type="button"
                      onClick={handleSelectAllPermissions}
                    >
                      Seleccionar todo
                    </button>

                    <button type="button" onClick={handleClearAllPermissions}>
                      Quitar todo
                    </button>
                  </div>
                </div>

                {isLoadingActions ? (
                  <div className={styles.InfoAlert}>Cargando acciones...</div>
                ) : actionsError ? (
                  <div className={styles.WarningAlert}>
                    {actionsError} Corre{" "}
                    <code>server/scripts/install-permission-scheme.js</code>{" "}
                    en Studio 3T para crear el esquema "{SACCIONES}" con sus
                    clasificaciones (Visualizar, Agregar, Modificar,
                    Eliminar, Aprobar) y poder asignar permisos.
                  </div>
                ) : (
                  <div className={styles.PermissionsLayout}>
                    <div className={styles.MenuTreePanel}>
                      {PERMISSION_MENU_TREE.map((node) =>
                        renderMenuTreeNode(node)
                      )}
                    </div>

                    <div className={styles.PermissionsDetailPanel}>
                      {!selectedMenuNode ? (
                        <div className={styles.InfoAlert}>
                          Selecciona un menú para asignar permisos.
                        </div>
                      ) : (
                        <>
                          <p className={styles.PermissionsDetailTitle}>
                            {selectedMenuNode.label}
                          </p>

                          <div className={styles.PermissionsDetailActions}>
                            {getAvailableActions(
                              selectedMenuNode,
                              actionList
                            ).map((action) => {
                              const actionKey = getActionKey(
                                selectedMenuNode.key,
                                action.key
                              );

                              return (
                                <label
                                  key={actionKey}
                                  className={styles.ActionCheckbox}
                                >
                                  <input
                                    type="checkbox"
                                    checked={formValues.actionPermissions.includes(
                                      actionKey
                                    )}
                                    onChange={() =>
                                      handleToggleAction(
                                        actionKey,
                                        selectedMenuNode.key,
                                        action.key
                                      )
                                    }
                                  />

                                  <span>{action.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "reports" && (
              <div className={styles.TabPanel}>
                <div className={styles.InfoAlert}>
                  No hay reportes disponibles para asignar permisos.
                </div>
              </div>
            )}
          </div>
        </section>
      </form>
    </section>
  );
}
